import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E08 — ÉGIDE. Le révélateur de build.
 *
 *  Un roguelite pose une question à laquelle il répond rarement : est-ce que mes
 *  choix de cartes ont produit autre chose qu'un plus gros nombre ? Tant que le
 *  bestiaire ne demande que des dégâts, la réponse est non — tous les builds
 *  sont le même build à des vitesses différentes.
 *
 *  L'Égide demande une FORME. Son bouclier absorbe tout ce qui arrive dans un
 *  cône de ±55° sous elle, c'est-à-dire précisément là où se trouve un joueur
 *  qui tire droit devant. Rien de ce qui augmente le DPS ne change ça. Ce qui le
 *  change, c'est la géométrie de la gerbe : une arme large touche les épaules
 *  sans y penser, une arme mono-cible doit se décaler d'une soixantaine
 *  d'unités et viser le bord. Deux joueurs de puissance identique n'ont pas le
 *  même combat.
 *
 *  ── Ce qu'elle enseigne ──
 *  Que la position de tir est une variable. Jusqu'ici, se placer servait à
 *  esquiver ; ici, se placer sert à TOUCHER. C'est la même compétence retournée,
 *  et c'est la dernière brique avant les boss à parties destructibles, où viser
 *  un point plutôt qu'une masse devient la norme.
 *
 *  ── Les deux routes, toutes deux valides ──
 *  Casser le bouclier (800 PV) puis le corps (520), ou contourner et frapper les
 *  520 directement. La première est lente et sûre, la seconde rapide et exigeante
 *  en placement. Aucune n'est un piège : c'est ce qui autorise l'Égide à être
 *  frustrante sans être injuste. Un joueur qui n'a pas compris le mécanisme
 *  gagne quand même, plus lentement, et comprend en regardant sa barre descendre.
 *
 *  ── Pourquoi son tir est presque inoffensif ──
 *  Deux projectiles visés toutes les 1,5 s : le minimum vital. L'Égide n'est pas
 *  un problème d'esquive, elle est un problème de placement, et lui ajouter un
 *  pattern dense forcerait le joueur à choisir entre bien se placer et survivre.
 *  Il choisirait de survivre, ne contournerait jamais, et l'ennemi n'aurait plus
 *  de raison d'exister. Sa pression doit rester juste assez forte pour interdire
 *  de rester immobile pendant qu'on cherche l'angle.
 *
 *  ⚠️ Elle a donc besoin d'ESCORTE pour être intéressante. Seule, elle est un
 *  exercice ; accompagnée de Faucheurs, elle devient un arbitrage entre esquiver
 *  et se placer, ce qui est tout autre chose. */
export default defineEnemy({
  id: 'egide',
  rank: Rank.Heavy,
  hp: 520,
  visualRadius: 46,
  score: 480,
  telegraph: 1.1,
  drops: 4,
  /* Station haute (Y = +380) : le joueur doit MONTER pour trouver l'angle, ce
     qui le sort de sa zone de confort en bas de terrain au moment même où il se
     concentre sur son placement. C'est là que l'escorte fait son travail. */
  motion: { kind: Motion.Hover, speed: 260, amplitude: 70, frequency: 0.6, holdY: 380 },
  /* 800 PV de bouclier pour 520 de corps : casser de front coûte 2,5 fois le
     prix du contournement. Le rapport est délibérément net — s'il était proche
     de 1, la question ne se poserait pas et le joueur tirerait tout droit sans
     jamais découvrir le mécanisme. */
  shieldHp: 800,
  /* ±55°. En dessous de ~45°, une gerbe même étroite passe à côté par accident
     et le bouclier devient décoratif ; au-delà de ~70°, il ne reste plus
     d'épaule atteignable et la seule route est la force brute. */
  shieldConeDeg: 55,
  emitter: {
    pattern: Pattern.Aimed,
    every: 1.5,
    count: 2,
    spreadDeg: 14,
    speed: 470,
    firstDelay: 1.5,
    family: Fam.EnemyPink,
  },
});
